import React from 'react';
import { Box, Inscription, Preloader } from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';
import { WarnModal } from '@/components/elements';
import { ErrorZone } from '@/modules/employer/components/EmployerReactivation/components/ErrorZone';
import EmployerReactivationBankList from '@/modules/employer/components/EmployerReactivation/EmployerReactivationBankList/EmployerReactivationBankList';
import { EmployerReactivationCardAndPlans } from '@/modules/employer/components/EmployerReactivation/EmployerReactivationCardAndPlans/EmployerReactivationCardAndPlans';

import { useReactivateEmployerModal } from './hooks/useReactivateEmployerModal';

const MODAL_DESCRIPTION = `Reactivate previously terminated employer and plans. 
All previous employer and plan settings will be used and can be modified once activated.`;

interface Props {
  onCancel?: () => void;
}

const EmployerReactivationModalForm: React.FC<Props> = ({ onCancel }) => {
  const {
    errors,
    hasFieldErrors,
    isLoading,
    isServerError,
    setIsServerError,
    handleCancel,
    handleSubmit,
  } = useReactivateEmployerModal({ onCancel });

  return (
    <>
      <Box margin={{ bottom: 'spacing24' }}>
        <Inscription textAlign="center" size="14px">
          {MODAL_DESCRIPTION}
        </Inscription>
      </Box>

      <Box data-testid="EmployerReactivationModalForm_container">
        <Box direction="column">

          <EmployerReactivationBankList hasFieldErrors={hasFieldErrors} />

          <EmployerReactivationCardAndPlans hasFieldErrors={hasFieldErrors} />

          <ErrorZone errors={errors} testId="EmployerReactivationModalForm_errors" />

          <Box
            direction="row"
            fill="horizontal"
            margin={{ top: 'spacing24' }}
            align="end"
            justify="end"
            width="medium"
            gap="xs"
          >
            <AppButton
              testId="EmployerReactivationModalForm_cancel"
              buttonType="secondary"
              width="150px"
              type="button"
              onClick={handleCancel}
            >
              Cancel
            </AppButton>
            <AppButton
              testId="EmployerReactivationModalForm_submit"
              type="button"
              onClick={handleSubmit}
              width="150px"
              disabled={isLoading}
            >
              {isLoading ? <Preloader /> : 'Confirm'}
            </AppButton>
          </Box>
        </Box>
      </Box>
      <WarnModal
        testId="employer-reactivation-server-error-modal"
        visible={isServerError}
        onSetVisible={setIsServerError}
        header="The server encountered an error processing the request"
        helptext={`It doesn't appear to have affected your data, but we cannot save the Employer reactivation with the latest records.
        Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
        buttonText="Close"
        onSubmit={handleSubmit}
        submitButtonText="Try again"
      />
    </>
  );
};

export default EmployerReactivationModalForm;
